import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./services.styles";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import CircleIcon from "@mui/icons-material/Circle";

const ServiceDropOffAnalysis = ({ data = [] }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const [activeIndex, setActiveIndex] = useState(null);

  // Process services data to get dropped leads per service
  const getDroppedByService = (servicesData) => {
    if (!servicesData || !Array.isArray(servicesData)) return [];

    return servicesData
      .map((service) => {
        const totalLeads = service.totalLeads || 0;
        const dropOffRate = service.dropOffRate || 0;
        const dropped = Math.round((totalLeads * dropOffRate) / 100);

        return {
          serviceName: service.name || "Unknown",
          dropped,
          totalLeads,
          dropOffRate,
        };
      })
      .filter((item) => item.dropped > 0)
      .sort((a, b) => b.dropped - a.dropped);
  };

  const droppedDataArray = getDroppedByService(data);

  // Calculate total dropped
  const totalDropped = droppedDataArray.reduce((sum, item) => sum + item.dropped, 0);

  // Create pie data with colors
  const COLORS = ["#EF4444", "#F87171", "#FCA5A5", "#DC2626", "#FB923C", "#F97316"];

  const pieData = droppedDataArray.map((item, index) => ({
    serviceName: item.serviceName,
    dropped: item.dropped,
    color: COLORS[index % COLORS.length],
  }));

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    const percentage = ((data.dropped / totalDropped) * 100).toFixed(1);

    return (
      <Box
        sx={{
          bgcolor: "#fff",
          px: 2,
          py: 1.5,
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          border: `1px solid ${colors.borderLight}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <CircleIcon sx={{ fontSize: 10, color: data.color }} />
          <Typography fontWeight={600} fontSize={13} color={colors.textPrimary}>
            {data.serviceName}
          </Typography>
        </Stack>
        <Typography fontSize={12} color="text.secondary">
          {data.dropped} dropped ({percentage}%)
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        height: 400,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        mt: 4,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: colors.textMuted,
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Pipeline Drop-off Analysis
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            Dropped leads by service
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase" }}>
            Total Dropped
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: colors.danger }}>
            {totalDropped.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {pieData.length > 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, flex: 1 }}>
          {/* Donut Chart */}
          <Box sx={{ position: "relative", width: 180, height: 180, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="dropped"
                  nameKey="serviceName"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.color}
                      opacity={activeIndex === null || activeIndex === index ? 1 : 0.4}
                      stroke="#fff"
                      strokeWidth={activeIndex === index ? 2 : 1}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  position={{ x: 0, y: -50 }}
                  wrapperStyle={{ zIndex: 10 }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Center Text */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: colors.danger,
                  lineHeight: 1,
                }}
              >
                {totalDropped}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mt: 0.5,
                }}
              >
                DROPPED
              </Typography>
            </Box>
          </Box>

          {/* Legend */}
          <Stack spacing={2} sx={{ flex: 1 }}>
            {droppedDataArray.map((item, index) => {
              const percentage = ((item.dropped / totalDropped) * 100).toFixed(1);
              return (
                <Stack
                  key={item.serviceName}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    cursor: "pointer",
                    opacity: activeIndex === null || activeIndex === index ? 1 : 0.5,
                    transition: "opacity 0.2s",
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CircleIcon sx={{ fontSize: 10, color: COLORS[index % COLORS.length] }} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textPrimary,
                      }}
                    >
                      {item.serviceName}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: colors.danger,
                      }}
                    >
                      {item.dropped}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: colors.textMuted,
                      }}
                    >
                      ({percentage}%)
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              color: colors.success,
            }}
          >
            0%
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
            }}
          >
            No leads dropped across {data.length} service{data.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceDropOffAnalysis;
